import { DocumentDownloadInputs } from '../../../__generated__/globalTypes';
import client from '../../config/GraphQLApolloClient';
import { DOWNLOAD_DOCUMENT_TLOG_INFO_ECAP } from './queries';
import JSZip from 'jszip';
import { saveAs } from 'save-as';
import { Buffer } from 'buffer';
import { DownloadDocumentEcapTLogInfoQuery, DownloadDocumentEcapTLogInfoQueryVariables } from './queries.generated';

type ElapsedTime = {
    hours: number;
    minutes: number;
    seconds: number;
};

function calculateElapsedTime(startTime:number, endTime:number):ElapsedTime {
    const elapsedTimeInSeconds = (endTime - startTime) / 1000;
    const hours = Math.floor(elapsedTimeInSeconds / 3600);
    const minutes = Math.floor((elapsedTimeInSeconds % 3600) / 60);
    const seconds = Math.floor(elapsedTimeInSeconds % 60);

    return {
        hours,
        minutes,
        seconds
    };
}

export type DownloadProgressProperties = {
    cancelling: boolean,
    progress:number
};

export type DownloadAndSaveParamProperties = {
    list: DocumentDownloadInputs[],
    fileName: string,
    batchSize?: number,
    downloadProgressProperties: DownloadProgressProperties
    progressNotifier?: () => void
};

function generateTimer(index:number, batchSize:number, maxIndex:number, downloadProgressProperties: DownloadProgressProperties, progressNotifier: () => void) {
    const TIMEOUT = 300_000;
    const NUMBER_OF_RUNS = 100;
    const INTERVAL = Math.floor(TIMEOUT / NUMBER_OF_RUNS);

    const nextIndex = Math.min(index + batchSize, maxIndex);

    let counter = 0;

    return setInterval(()=> {
        if (!downloadProgressProperties.cancelling) {
            counter+=1;
            const offset = Math.floor((nextIndex-index) *(Math.log(counter)/Math.log(100)));

            const progress = Math.round((index + offset) / maxIndex * 100);
            console.debug(`Running timer with params ${index}, next index ${nextIndex}, offset ${offset} and counter ${counter} setting progress to ${progress}`);
            downloadProgressProperties.progress = progress;
            if (progressNotifier) {
                progressNotifier()
            }
        }
    },INTERVAL);
}
const PARALLEL_TASKS = 4;
const MAX_RETRIES = 9;

async function downloadBatchWithRetry(
    documentBatch: DocumentDownloadInputs[],
): Promise<{ blob: Blob | null; errorMessage: string | null; successDocs: any[]; failureDocs: any[] }> {
    let lastError: any;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
            const response = await client.query<DownloadDocumentEcapTLogInfoQuery, DownloadDocumentEcapTLogInfoQueryVariables>({
                query: DOWNLOAD_DOCUMENT_TLOG_INFO_ECAP,
                variables: { documentDownloadInputs: documentBatch },
                fetchPolicy: 'network-only',
            });

            const data = response.data.downloadDocumentEcapTLogInfo;
            const successDocs = data.successDocuments ?? [];
            const failureDocs = data.failureDocuments ?? [];

            if (!data.encodedZipFileData) {
                return { blob: null, errorMessage: 'Encoded zip file data is empty', successDocs, failureDocs };
            }

            if (data.errorMessage) {
                return { blob: null, errorMessage: data.errorMessage, successDocs, failureDocs };
            }

            const decodedData = Buffer.from(data.encodedZipFileData, 'base64');
            return { blob: new Blob([decodedData], { type: 'application/zip' }), errorMessage: null, successDocs, failureDocs };
        } catch (error) {
            lastError = error;
            console.warn(`Batch download attempt ${attempt + 1} of ${MAX_RETRIES} failed`, error);
            if (attempt < MAX_RETRIES - 1) {
                await new Promise<void>(resolve => setTimeout(resolve, Math.min(25000, 5000 * (attempt + 1))));
            }
        }
    }
    throw lastError;
}

export async function downloadAndSaveZipDocuments(param:DownloadAndSaveParamProperties) {
    if (!param?.list?.length || !param?.fileName || !param.downloadProgressProperties) {
        throw Error("The specified param is invalid. Mandatory fields, list and fileName expected");
    }

    const list = param.list;
    const fileName = param.fileName;
    const batchSize = param.batchSize || 25;
    const downloadProgressProperties = param.downloadProgressProperties;
    const progressNotifier = param.progressNotifier;

    downloadProgressProperties.cancelling = false;

    const zip = new JSZip();
    const successList: any[] = [];
    const failureList: any[] = [];

    const startTime = new Date().getTime();

    // Split the full list into batches upfront
    const allBatches: DocumentDownloadInputs[][] = [];
    for (let i = 0; i < list.length; i += batchSize) {
        allBatches.push(list.slice(i, Math.min(i + batchSize, list.length)));
    }

    // Process batches in parallel groups of PARALLEL_TASKS
    for (let groupStart = 0; groupStart < allBatches.length; groupStart += PARALLEL_TASKS) {
        if (downloadProgressProperties.cancelling) break;

        const groupBatches = allBatches.slice(groupStart, Math.min(groupStart + PARALLEL_TASKS, allBatches.length));
        const docStartIndex = groupStart * batchSize;
        const groupDocCount = groupBatches.reduce((sum, b) => sum + b.length, 0);

        downloadProgressProperties.progress = Math.round((docStartIndex / list.length) * 100);

        const elapsed = calculateElapsedTime(startTime, new Date().getTime());
        console.log(`Downloading group starting at batch ${groupStart} (${groupBatches.length} parallel tasks). Elapsed: ${elapsed.hours}h ${elapsed.minutes}m ${elapsed.seconds}s. Progress: ${downloadProgressProperties.progress}%`);

        if (progressNotifier) progressNotifier();

        const interval = generateTimer(docStartIndex, groupDocCount, list.length, downloadProgressProperties, progressNotifier);

        let groupResults: Awaited<ReturnType<typeof downloadBatchWithRetry>>[];
        try {
            groupResults = await Promise.all(groupBatches.map(batch => downloadBatchWithRetry(batch)));
        } catch (error) {
            clearInterval(interval);
            throw error;
        }

        clearInterval(interval);

        // Accumulate results and unzip blobs
        for (const result of groupResults) {
            successList.push(...result.successDocs);
            failureList.push(...result.failureDocs);

            if (result.errorMessage) {
                continue;
            }

            const unzipper = new JSZip();
            const listOfFiles = await unzipper.loadAsync(result.blob).then((contents) => {
                const files = Object.keys(contents.files).filter(filename => !contents.files[filename].dir);
                return Promise.allSettled(
                    files.map(filename =>
                        contents.files[filename].async('blob').then(fileData => ({ filename, data: fileData }))
                    )
                ).then(results =>
                    results
                        .filter((r): r is PromiseFulfilledResult<{ filename: string; data: Blob }> => {
                            if (r.status === 'rejected') {
                                console.warn('Skipping file during unzip, reason:', r.reason);
                            }
                            return r.status === 'fulfilled';
                        })
                        .map(r => r.value)
                );
            });

            listOfFiles.forEach(file => zip.file(file.filename, file.data));
        }
    }

    if (!downloadProgressProperties.cancelling) {
        //Business want to see the 100% completion before they see the save file dialog....
        downloadProgressProperties.progress = 99;
        if (progressNotifier) progressNotifier();

        await new Promise<void>(resolve => setTimeout(resolve, 2000));

        const result = await zip.generateAsync({type: 'blob'});
        saveAs(result, fileName);
    }

    downloadProgressProperties.progress = 100;
    if (progressNotifier) progressNotifier();

    return {
        data: {
            downloadDocumentEcap: {
                successDocuments: successList,
                failureDocuments: failureList
            }
        }
    }
}